import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const Device = () => {
    const [devices, setDevices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [selectedDevice, setSelectedDevice] = useState(null);
    const [connectionStatus, setConnectionStatus] = useState({}); // Store connection status for each device
    const [testingConnections, setTestingConnections] = useState({}); // Track which devices are being tested

    const [formData, setFormData] = useState({
        device_name: '',
        ip_address: '',
        port: '',
        location: '',
        is_active: 1
    });

    const [validationErrors, setValidationErrors] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('name');
    const [filterActive, setFilterActive] = useState('all');

    const [userPermissions, setUserPermissions] = useState({
        can_view: false,
        can_add: false,
        can_edit: false,
        can_delete: false,
        can_export: false,
        loaded: false
    });

    const navigate = useNavigate();
    const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000';

    const fetchUserPermissions = useCallback(async () => {
        try {
            const token = localStorage.getItem('authToken');
            if (!token) {
                setUserPermissions({
                    can_view: false,
                    can_add: false,
                    can_edit: false,
                    can_delete: false,
                    can_export: false,
                    loaded: true
                });
                return;
            }

            const response = await axios.get(`${API_BASE}/get_user_permissions.php`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success && response.data.permissions) {
                const devicePermissions = response.data.permissions.find(
                    p => p.module_name === 'Device' || p.module_name === 'BiometricDevices'
                );

                if (devicePermissions) {
                    setUserPermissions({
                        can_view: Boolean(devicePermissions.can_view),
                        can_add: Boolean(devicePermissions.can_add),
                        can_edit: Boolean(devicePermissions.can_edit),
                        can_delete: Boolean(devicePermissions.can_delete),
                        can_export: Boolean(devicePermissions.can_export),
                        loaded: true
                    });
                } else {
                    setUserPermissions({
                        can_view: false,
                        can_add: false,
                        can_edit: false,
                        can_delete: false,
                        can_export: false,
                        loaded: true
                    });
                }
            } else {
                setUserPermissions({
                    can_view: false,
                    can_add: false,
                    can_edit: false,
                    can_delete: false,
                    can_export: false,
                    loaded: true
                });
            }
        } catch (error) {
            console.error('Error fetching permissions:', error);
            setUserPermissions({
                can_view: false,
                can_add: false,
                can_edit: false,
                can_delete: false,
                can_export: false,
                loaded: true
            });
        }
    }, [API_BASE]);

    const fetchDevices = useCallback(async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('authToken');
            if (!token) {
                setError('No authentication token found');
                setLoading(false);
                return;
            }

            const response = await axios.get(`${API_BASE}device/get_devices.php`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                timeout: 15000 // 15 second timeout (increased from 10s for better reliability)
            });

            if (response.data && response.data.success) {
                const devicesData = response.data.devices || [];
                setDevices(devicesData);
                setError('');

                // Initialize connection status for all devices as "unknown"
                const initialStatus = {};
                devicesData.forEach(device => {
                    initialStatus[device.device_id] = {
                        status: 'unknown',
                        lastChecked: null,
                        loading: false
                    };
                });
                setConnectionStatus(initialStatus);
            } else {
                setDevices([]);
                const errorMsg = response.data?.error || 'Failed to fetch devices';
                setError(errorMsg);
            }
        } catch (error) {
            setDevices([]);
            const errorMsg = error.response?.data?.error ||
                error.message ||
                'Failed to fetch devices';
            setError(errorMsg);
            console.error('Error fetching devices:', error);
        } finally {
            setLoading(false);
        }
    }, [API_BASE]);

    // Function to test device connection
    const testDeviceConnection = useCallback(async (device) => {
        try {
            // Set loading state for this device
            setTestingConnections(prev => ({
                ...prev,
                [device.device_id]: true
            }));

            const token = localStorage.getItem('authToken');
            const response = await axios.post(`${API_BASE}device/test_device_connection.php`,
                {
                    device_id: device.device_id,
                    ip_address: device.ip_address,
                    port: device.port
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    timeout: 15000 // 15 second timeout for connection test (increased from 10s)
                }
            );

            // Update connection status
            setConnectionStatus(prev => ({
                ...prev,
                [device.device_id]: {
                    status: response.data.success ? 'online' : 'offline',
                    lastChecked: new Date().toISOString(),
                    message: response.data.message || response.data.error,
                    loading: false
                }
            }));

            return response.data;
        } catch (error) {
            console.error('Error testing device connection:', error);

            // Determine error type for better messaging
            let errorMessage = 'Connection failed';
            if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
                errorMessage = `Connection timeout - Device at ${device.ip_address}:${device.port} did not respond within 15 seconds`;
            } else if (error.code === 'ERR_NETWORK' || error.message?.includes('Network Error')) {
                errorMessage = `Network error - Unable to reach ${device.ip_address}:${device.port}`;
            } else if (error.response?.status === 401) {
                errorMessage = 'Authentication failed - Please login again';
            } else if (error.response?.status === 403) {
                errorMessage = 'Access denied - Insufficient permissions';
            } else if (error.response?.data?.error) {
                errorMessage = error.response.data.error;
            } else if (error.message) {
                errorMessage = error.message;
            }

            // Update connection status as offline on error
            setConnectionStatus(prev => ({
                ...prev,
                [device.device_id]: {
                    status: 'offline',
                    lastChecked: new Date().toISOString(),
                    message: errorMessage,
                    loading: false
                }
            }));

            return {
                success: false,
                error: errorMessage
            };
        } finally {
            // Clear loading state
            setTestingConnections(prev => ({
                ...prev,
                [device.device_id]: false
            }));
        }
    }, [API_BASE]);

    // Test all devices connections
    const testAllConnections = useCallback(async () => {
        const results = [];
        for (const device of devices) {
            if (device.is_active) { // Only test active devices
                const result = await testDeviceConnection(device);
                results.push({
                    device_id: device.device_id,
                    device_name: device.device_name,
                    ...result
                });
            }
        }
        return results;
    }, [devices, testDeviceConnection]);

    // Test connection for a single device from UI
    const handleTestConnection = useCallback(async (device) => {
        const result = await testDeviceConnection(device);
        if (result.success) {
            alert(`Device connection successful: ${result.message}`);
        } else {
            alert(`Device connection failed: ${result.error}`);
        }
    }, [testDeviceConnection]);

    const canViewDevices = useCallback(() => userPermissions.can_view, [userPermissions.can_view]);
    const canAddDevice = useCallback(() => userPermissions.can_add, [userPermissions.can_add]);
    const canEditDevice = useCallback(() => userPermissions.can_edit, [userPermissions.can_edit]);
    const canDeleteDevice = useCallback(() => userPermissions.can_delete, [userPermissions.can_delete]);
    const canExportDevices = useCallback(() => userPermissions.can_export, [userPermissions.can_export]);

    useEffect(() => {
        const initializeData = async () => {
            await fetchUserPermissions();
            await fetchDevices();
        };

        initializeData();
    }, [fetchUserPermissions, fetchDevices]);

    // Auto-test connections when devices are loaded
    useEffect(() => {
        if (devices.length > 0 && canViewDevices()) {
            // Test connections for active devices after a short delay
            const timer = setTimeout(() => {
                const activeDevices = devices.filter(d => d.is_active);
                if (activeDevices.length > 0) {
                    console.log('Testing connections for', activeDevices.length, 'active devices');
                    testAllConnections();
                }
            }, 1000); // 1 second delay after loading

            return () => clearTimeout(timer);
        }
    }, [devices, canViewDevices, testAllConnections]);

    const handleAdd = () => {
        if (!canAddDevice()) {
            alert('You do not have permission to add devices');
            return;
        }
        setFormData({
            device_name: '',
            ip_address: '',
            port: '',
            location: '',
            is_active: 1
        });
        setValidationErrors({});
        setShowAddModal(true);
    };

    const handleEdit = (device) => {
        if (!canEditDevice()) {
            alert('You do not have permission to edit devices');
            return;
        }
        setSelectedDevice(device);
        setFormData({
            device_name: device.device_name,
            ip_address: device.ip_address,
            port: device.port,
            location: device.location,
            is_active: device.is_active ? 1 : 0
        });
        setValidationErrors({});
        setShowEditModal(true);
    };

    const handleView = (device) => {
        setSelectedDevice(device);
        setShowViewModal(true);
    };

    const handleDelete = (device) => {
        if (!canDeleteDevice()) {
            alert('You do not have permission to delete devices');
            return;
        }
        setSelectedDevice(device);
        setShowDeleteModal(true);
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        let processedValue = value;
        if (type === 'checkbox') {
            processedValue = checked ? 1 : 0;
        }

        setFormData(prev => ({
            ...prev,
            [name]: processedValue
        }));

        if (validationErrors[name]) {
            setValidationErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const validateForm = () => {
        const errors = {};

        if (!formData.device_name.trim()) {
            errors.device_name = 'Device name is required';
        } else if (formData.device_name.length > 100) {
            errors.device_name = 'Device name cannot exceed 100 characters';
        }

        if (!formData.ip_address.trim()) {
            errors.ip_address = 'IP address is required';
        } else {
            const ipPattern = /^(\d{1,3}\.){3}\d{1,3}$/;
            if (!ipPattern.test(formData.ip_address)) {
                errors.ip_address = 'Invalid IP address format (e.g., 192.168.1.1)';
            }
        }

        if (!formData.port.trim()) {
            errors.port = 'Port is required';
        } else if (!/^\d+$/.test(formData.port)) {
            errors.port = 'Port must be a number';
        } else if (parseInt(formData.port) < 1 || parseInt(formData.port) > 65535) {
            errors.port = 'Port must be between 1 and 65535';
        }

        if (formData.location && formData.location.length > 100) {
            errors.location = 'Location cannot exceed 100 characters';
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleAddSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.post(`${API_BASE}device/add_device.php`,
                formData,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                await fetchDevices();
                setShowAddModal(false);
                alert('Device added successfully');
            } else {
                setError(response.data.error || 'Failed to add device');
            }
        } catch (error) {
            setError(error.response?.data?.error || 'Failed to add device');
            console.error('Error adding device:', error);
        }
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.post(`${API_BASE}device/update_device.php`,
                {
                    device_id: selectedDevice.device_id,
                    ...formData
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                await fetchDevices();
                setShowEditModal(false);
                alert('Device updated successfully');
            } else {
                setError(response.data.error || 'Failed to update device');
            }
        } catch (error) {
            setError(error.response?.data?.error || 'Failed to update device');
            console.error('Error updating device:', error);
        }
    };

    const handleDeleteConfirm = async () => {
        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.post(`${API_BASE}device/delete_device.php`,
                { device_id: selectedDevice.device_id },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                await fetchDevices();
                setShowDeleteModal(false);
                alert('Device deleted successfully');
            } else {
                setError(response.data.error || 'Failed to delete device');
            }
        } catch (error) {
            setError(error.response?.data?.error || 'Failed to delete device');
            console.error('Error deleting device:', error);
        }
    };

    const toggleDeviceStatus = async (deviceId, currentStatus) => {
        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.post(`${API_BASE}device/toggle_device_status.php`,
                {
                    device_id: deviceId,
                    is_active: currentStatus ? 0 : 1
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                await fetchDevices();
                alert('Device status updated successfully');
            } else {
                setError(response.data.error || 'Failed to update device status');
            }
        } catch (error) {
            setError(error.response?.data?.error || 'Failed to update device status');
            console.error('Error toggling device status:', error);
        }
    };

    const getStatusBadge = (isActive) => {
        return isActive ?
            <span className="badge bg-success">Active</span> :
            <span className="badge bg-danger">Inactive</span>;
    };

    const getConnectionBadge = (device) => {
        const status = connectionStatus[device.device_id];

        if (!status || status.status === 'unknown') {
            return <span className="badge bg-secondary">Unknown</span>;
        }

        if (testingConnections[device.device_id]) {
            return <span className="badge bg-warning">Testing...</span>;
        }

        if (status.status === 'online') {
            return <i className="bi bi-lightbulb-fill text-success fs-5" title="Online"></i>;
        } else {
            return <i className="bi bi-lightbulb-fill text-danger fs-5" title="Offline"></i>;
        }
    };

    const getLastCheckedTime = (device) => {
        const status = connectionStatus[device.device_id];
        if (!status || !status.lastChecked) return 'Never';

        const timeDiff = new Date() - new Date(status.lastChecked);
        const minutes = Math.floor(timeDiff / 60000);

        if (minutes < 1) return 'Just now';
        if (minutes < 60) return `${minutes} min ago`;

        const hours = Math.floor(minutes / 60);
        if (hours < 24) return `${hours} hr ago`;

        const days = Math.floor(hours / 24);
        return `${days} days ago`;
    };

    const getDeviceIcon = (deviceName) => {
        const lowerName = deviceName.toLowerCase();
        if (lowerName.includes('zkt')) return 'bi-cpu';
        if (lowerName.includes('honeywell')) return 'bi-fingerprint';
        if (lowerName.includes('suprema')) return 'bi-shield-check';
        if (lowerName.includes('matrix')) return 'bi-device-hdd';
        return 'bi-pc-display';
    };

    const filteredDevices = devices.filter(device => {
        const matchesSearch =
            device.device_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            device.ip_address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            device.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            device.port?.includes(searchTerm);

        const matchesFilter =
            filterActive === 'all' ||
            (filterActive === 'active' && device.is_active) ||
            (filterActive === 'inactive' && !device.is_active);

        return matchesSearch && matchesFilter;
    });

    const sortedDevices = [...filteredDevices].sort((a, b) => {
        switch (sortBy) {
            case 'name':
                return a.device_name.localeCompare(b.device_name);
            case 'ip':
                return a.ip_address.localeCompare(b.ip_address);
            case 'location':
                return a.location.localeCompare(b.location);
            case 'status':
                return (b.is_active ? 1 : 0) - (a.is_active ? 1 : 0);
            case 'connection':
                const statusA = connectionStatus[a.device_id]?.status || 'unknown';
                const statusB = connectionStatus[b.device_id]?.status || 'unknown';
                const order = { 'online': 1, 'unknown': 2, 'offline': 3 };
                return (order[statusA] || 4) - (order[statusB] || 4);
            default:
                return a.device_id - b.device_id;
        }
    });

    if (loading || !userPermissions.loaded) {
        return (
            <div className="container-fluid py-4">
                <div className="row">
                    <div className="col-12">
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mt-3 text-muted">Loading devices...</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!canViewDevices()) {
        return (
            <div className="container-fluid py-4">
                <div className="row">
                    <div className="col-12">
                        <div className="card shadow-sm border-0">
                            <div className="card-body text-center py-5">
                                <i className="bi bi-shield-exclamation display-1 text-warning"></i>
                                <h3 className="mt-3 text-warning">Access Denied</h3>
                                <p className="text-muted">You do not have permission to view biometric devices.</p>
                                <button
                                    className="btn btn-primary mt-3"
                                    onClick={() => navigate('/dashboard')}
                                >
                                    <i className="bi bi-speedometer2 me-2"></i>
                                    Back to Dashboard
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid py-4">
            {/* Header */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="card shadow-sm border-0">
                        <div className="card-body py-4">
                            <div className="row align-items-center">
                                <div className="col-md-6">
                                    <div className="d-flex align-items-center">
                                        <div className="btn-group me-3" role="group">
                                            <button
                                                className="btn btn-outline-secondary btn-sm"
                                                onClick={() => navigate('/admin-panel')}
                                                title="Go to Dashboard"
                                            >
                                                <i className="bi bi-speedometer2"></i>
                                            </button>
                                            <button
                                                className="btn btn-outline-secondary btn-sm"
                                                onClick={() => navigate('/Menu')}
                                                title="Go to Menu"
                                            >
                                                <i className="bi bi-menu-button"></i>
                                            </button>
                                        </div>
                                        <div>
                                            <h2 className="h4 mb-1 fw-bold text-dark">
                                                <i className="bi bi-device-hdd me-2 text-primary"></i>
                                                Biometric Devices
                                                {!canAddDevice() && !canEditDevice() && !canDeleteDevice() && (
                                                    <span className="badge bg-warning ms-2">View Only</span>
                                                )}
                                            </h2>
                                            <p className="text-muted mb-0">
                                                Manage attendance devices and connections
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-6 text-end">
                                    <div className="d-flex justify-content-end align-items-center">
                                        <button
                                            className="btn btn-outline-secondary me-2"
                                            onClick={() => navigate('/admin-panel')}
                                        >
                                            <i className="bi bi-arrow-left me-2"></i>
                                            Back to Dashboard
                                        </button>
                                        {canAddDevice() && (
                                            <button
                                                className="btn btn-primary text-white"
                                                onClick={handleAdd}
                                            >
                                                <i className="bi bi-plus-circle me-2"></i>
                                                Add Device
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Quick Navigation */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="card bg-light border-0">
                        <div className="card-body py-3">
                            <div className="row text-center">
                                <div className="col-md-2 mb-2 mb-md-0">
                                    <button
                                        className="btn btn-outline-primary w-100 d-flex align-items-center justify-content-center"
                                        onClick={() => navigate('/admin-panel')}
                                    >
                                        <i className="bi bi-speedometer2 me-2"></i>
                                        Dashboard
                                    </button>
                                </div>
                                <div className="col-md-2 mb-2 mb-md-0">
                                    <button
                                        className="btn btn-outline-success w-100 d-flex align-items-center justify-content-center"
                                        onClick={() => navigate('/employees')}
                                    >
                                        <i className="bi bi-people me-2"></i>
                                        Employees
                                    </button>
                                </div>
                                <div className="col-md-2 mb-2 mb-md-0">
                                    <button
                                        className="btn btn-outline-info w-100 d-flex align-items-center justify-content-center"
                                        onClick={() => navigate('/shifts')}
                                    >
                                        <i className="bi bi-clock-history me-2"></i>
                                        Shifts
                                    </button>
                                </div>
                                <div className="col-md-2 mb-2 mb-md-0">
                                    <button
                                        className="btn btn-outline-warning w-100 d-flex align-items-center justify-content-center"
                                        onClick={() => navigate('/policies')}
                                    >
                                        <i className="bi bi-shield-check me-2"></i>
                                        Policies
                                    </button>
                                </div>
                                <div className="col-md-2 mb-2 mb-md-0">
                                    <button
                                        className="btn btn-primary text-white w-100 d-flex align-items-center justify-content-center"
                                        disabled
                                    >
                                        <i className="bi bi-device-hdd me-2"></i>
                                        Devices
                                    </button>
                                </div>
                                <div className="col-md-2 mb-2 mb-md-0">
                                    <button
                                        className="btn btn-outline-dark w-100 d-flex align-items-center justify-content-center"
                                        onClick={() => navigate('/Menu')}
                                    >
                                        <i className="bi bi-menu-button me-2"></i>
                                        Menu
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Error Alert */}
            {error && (
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="alert alert-danger d-flex align-items-center" role="alert">
                            <i className="bi bi-exclamation-triangle-fill me-2"></i>
                            <div>
                                <strong>Error:</strong> {error}
                                <div className="mt-2">
                                    <button
                                        className="btn btn-sm btn-outline-danger me-2"
                                        onClick={fetchDevices}
                                    >
                                        <i className="bi bi-arrow-clockwise me-1"></i>
                                        Retry
                                    </button>
                                    <button
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={() => setError('')}
                                    >
                                        Dismiss
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Search and Filter Bar */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="card shadow-sm border-0">
                        <div className="card-body">
                            <div className="row g-3 align-items-center">
                                <div className="col-md-3">
                                    <div className="input-group">
                                        <span className="input-group-text bg-light border-end-0">
                                            <i className="bi bi-search text-muted"></i>
                                        </span>
                                        <input
                                            type="text"
                                            className="form-control border-start-0"
                                            placeholder="Search devices..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                        {searchTerm && (
                                            <button
                                                className="btn btn-outline-secondary border-start-0"
                                                onClick={() => setSearchTerm('')}
                                            >
                                                <i className="bi bi-x"></i>
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <div className="col-md-2">
                                    <select
                                        className="form-select"
                                        value={filterActive}
                                        onChange={(e) => setFilterActive(e.target.value)}
                                    >
                                        <option value="all">All Status</option>
                                        <option value="active">Active Only</option>
                                        <option value="inactive">Inactive Only</option>
                                    </select>
                                </div>
                                <div className="col-md-2">
                                    <select
                                        className="form-select"
                                        value={sortBy}
                                        onChange={(e) => setSortBy(e.target.value)}
                                    >
                                        <option value="name">Sort by Name</option>
                                        <option value="ip">Sort by IP</option>
                                        <option value="location">Sort by Location</option>
                                        <option value="status">Sort by Status</option>
                                        <option value="connection">Sort by Connection</option>
                                    </select>
                                </div>
                                <div className="col-md-5 text-end">
                                    <div className="d-flex justify-content-end align-items-center">
                                        <button
                                            className="btn btn-outline-secondary me-2"
                                            onClick={fetchDevices}
                                            title="Refresh Devices List"
                                        >
                                            <i className="bi bi-arrow-clockwise"></i>
                                        </button>
                                        <button
                                            className="btn btn-outline-info me-2"
                                            onClick={testAllConnections}
                                            title="Test All Connections"
                                            disabled={devices.filter(d => d.is_active).length === 0}
                                        >
                                            <i className="bi bi-wifi"></i>
                                            <span className="ms-1 d-none d-md-inline">Test All</span>
                                        </button>
                                        {canExportDevices() && (
                                            <button
                                                className="btn btn-outline-success me-2"
                                                onClick={() => alert('Export functionality coming soon!')}
                                                title="Export Devices"
                                            >
                                                <i className="bi bi-download"></i>
                                            </button>
                                        )}
                                        {canAddDevice() && (
                                            <button
                                                className="btn btn-primary text-white"
                                                onClick={handleAdd}
                                            >
                                                <i className="bi bi-plus-circle me-2"></i>
                                                Add Device
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Devices Grid */}
            <div className="row">
                {sortedDevices.length === 0 ? (
                    <div className="col-12">
                        <div className="card shadow-sm border-0">
                            <div className="card-body text-center py-5">
                                <i className="bi bi-device-hdd display-1 text-muted"></i>
                                <h4 className="mt-3 text-muted">No devices found</h4>
                                <p className="text-muted">
                                    {devices.length === 0
                                        ? "No biometric devices available. Add your first device."
                                        : "No devices match your search criteria."
                                    }
                                </p>
                                {devices.length === 0 && canAddDevice() && (
                                    <button
                                        className="btn btn-primary text-white mt-2"
                                        onClick={handleAdd}
                                    >
                                        <i className="bi bi-plus-circle me-2"></i>
                                        Add First Device
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                ) : (
                    sortedDevices.map((device) => (
                        <div className="col-md-6 col-lg-4 mb-4" key={device.device_id}>
                            <div className="card shadow-sm border-0 h-100">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between align-items-start mb-3">
                                        <div>
                                            <h5 className="card-title mb-1">
                                                <i className={`bi ${getDeviceIcon(device.device_name)} me-2 text-primary`}></i>
                                                {device.device_name}
                                                <span className="badge bg-light text-dark border ms-2">
                                                    ID: {device.device_id}
                                                </span>
                                            </h5>
                                            <span className="badge bg-secondary">
                                                {device.location || 'No Location'}
                                            </span>
                                        </div>
                                        <div>
                                            {getStatusBadge(device.is_active)}
                                        </div>
                                    </div>

                                    <div className="mb-3">
                                        <div className="row small g-2 mb-2">
                                            <div className="col-6">
                                                <span className="text-muted">IP Address:</span>
                                                <br />
                                                <strong className="text-primary">{device.ip_address}</strong>
                                            </div>
                                            <div className="col-6">
                                                <span className="text-muted">Port:</span>
                                                <br />
                                                <strong>{device.port}</strong>
                                            </div>
                                        </div>
                                        <div className="row small g-2 mb-2">
                                            <div className="col-6">
                                                <span className="text-muted">Connection:</span>
                                                <br />
                                                {getConnectionBadge(device)}
                                            </div>
                                            <div className="col-6">
                                                <span className="text-muted">Last Checked:</span>
                                                <br />
                                                <small className="text-muted">{getLastCheckedTime(device)}</small>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="d-flex justify-content-between align-items-center">
                                        <small className="text-muted">
                                            {device.ip_address}:{device.port}
                                        </small>
                                        <div className="btn-group">
                                            <button
                                                className="btn btn-outline-info btn-sm"
                                                onClick={() => handleView(device)}
                                                title="View Details"
                                            >
                                                <i className="bi bi-eye"></i>
                                            </button>
                                            {canEditDevice() && (
                                                <>
                                                    <button
                                                        className="btn btn-outline-primary btn-sm"
                                                        onClick={() => handleEdit(device)}
                                                        title="Edit Device"
                                                    >
                                                        <i className="bi bi-pencil"></i>
                                                    </button>
                                                    <button
                                                        className="btn btn-outline-warning btn-sm"
                                                        onClick={() => handleTestConnection(device)}
                                                        title="Test Connection"
                                                        disabled={testingConnections[device.device_id] || !device.is_active}
                                                    >
                                                        <i className={`bi ${testingConnections[device.device_id] ? 'bi-arrow-clockwise' : 'bi-wifi'}`}></i>
                                                    </button>
                                                </>
                                            )}
                                            {canDeleteDevice() && (
                                                <button
                                                    className="btn btn-outline-danger btn-sm"
                                                    onClick={() => handleDelete(device)}
                                                    title="Delete Device"
                                                >
                                                    <i className="bi bi-trash"></i>
                                                </button>
                                            )}
                                            {canEditDevice() && (
                                                <button
                                                    className="btn btn-outline-secondary btn-sm"
                                                    onClick={() => toggleDeviceStatus(device.device_id, device.is_active)}
                                                    title={device.is_active ? 'Deactivate' : 'Activate'}
                                                >
                                                    <i className={`bi bi-power ${device.is_active ? 'text-success' : 'text-danger'}`}></i>
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Stats Footer */}
            <div className="row mt-4">
                <div className="col-12">
                    <div className="card bg-light border-0">
                        <div className="card-body py-3">
                            <div className="row text-center">
                                <div className="col-md-3">
                                    <div className="fw-bold text-primary fs-4">{devices.length}</div>
                                    <small className="text-muted">Total Devices</small>
                                </div>
                                <div className="col-md-3">
                                    <div className="fw-bold text-success fs-4">
                                        {devices.filter(d => d.is_active).length}
                                    </div>
                                    <small className="text-muted">Active Devices</small>
                                </div>
                                <div className="col-md-3">
                                    <div className="fw-bold text-success fs-4">
                                        {devices.filter(d => {
                                            const status = connectionStatus[d.device_id];
                                            return status && status.status === 'online';
                                        }).length}
                                    </div>
                                    <small className="text-muted">Online Now</small>
                                </div>
                                <div className="col-md-3">
                                    <div className="fw-bold text-info fs-4">
                                        {devices.filter(d => parseInt(d.port) === 4370).length}
                                    </div>
                                    <small className="text-muted">Default Port (4370)</small>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Quick Actions Footer */}
            <div className="row mt-4">
                <div className="col-12">
                    <div className="card border-0">
                        <div className="card-body text-center py-3">
                            <div className="btn-group" role="group">
                                <button
                                    className="btn btn-outline-primary"
                                    onClick={fetchDevices}
                                >
                                    <i className="bi bi-arrow-clockwise me-2"></i>
                                    Refresh List
                                </button>
                                <button
                                    className="btn btn-outline-info"
                                    onClick={testAllConnections}
                                    disabled={devices.filter(d => d.is_active).length === 0}
                                >
                                    <i className="bi bi-wifi me-2"></i>
                                    Test All Connections
                                </button>
                                <button
                                    className="btn btn-outline-success"
                                    onClick={() => navigate('/employees')}
                                >
                                    <i className="bi bi-people me-2"></i>
                                    Manage Employees
                                </button>
                                {canAddDevice() && (
                                    <button
                                        className="btn btn-primary text-white"
                                        onClick={handleAdd}
                                    >
                                        <i className="bi bi-plus-circle me-2"></i>
                                        Add New Device
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Add Device Modal */}
            {showAddModal && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header" style={{
                                background: 'linear-gradient(135deg, #0d6efd 0%, #0b5ed7 100%)',
                                borderBottom: 'none'
                            }}>
                                <h5 className="modal-title text-white">
                                    <i className="bi bi-plus-circle me-2"></i>
                                    Add New Biometric Device
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    onClick={() => setShowAddModal(false)}
                                ></button>
                            </div>

                            <form onSubmit={handleAddSubmit}>
                                <div className="modal-body">
                                    <div className="mb-3">
                                        <label htmlFor="device_name" className="form-label fw-medium">
                                            Device Name <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-control ${validationErrors.device_name ? 'is-invalid' : ''}`}
                                            id="device_name"
                                            name="device_name"
                                            value={formData.device_name}
                                            onChange={handleChange}
                                            placeholder="e.g., Main Entrance ZKTeco"
                                            required
                                        />
                                        {validationErrors.device_name && (
                                            <div className="invalid-feedback">{validationErrors.device_name}</div>
                                        )}
                                        <div className="form-text">Name to identify the device</div>
                                    </div>

                                    <div className="row">
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <label htmlFor="ip_address" className="form-label fw-medium">
                                                    IP Address <span className="text-danger">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    className={`form-control ${validationErrors.ip_address ? 'is-invalid' : ''}`}
                                                    id="ip_address"
                                                    name="ip_address"
                                                    value={formData.ip_address}
                                                    onChange={handleChange}
                                                    placeholder="192.168.1.100"
                                                    required
                                                />
                                                {validationErrors.ip_address && (
                                                    <div className="invalid-feedback">{validationErrors.ip_address}</div>
                                                )}
                                                <div className="form-text">Device IP address</div>
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <label htmlFor="port" className="form-label fw-medium">
                                                    Port <span className="text-danger">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    className={`form-control ${validationErrors.port ? 'is-invalid' : ''}`}
                                                    id="port"
                                                    name="port"
                                                    value={formData.port}
                                                    onChange={handleChange}
                                                    placeholder="4370"
                                                    required
                                                />
                                                {validationErrors.port && (
                                                    <div className="invalid-feedback">{validationErrors.port}</div>
                                                )}
                                                <div className="form-text">Default: 4370 for ZKTeco</div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mb-3">
                                        <label htmlFor="location" className="form-label fw-medium">
                                            Location
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-control ${validationErrors.location ? 'is-invalid' : ''}`}
                                            id="location"
                                            name="location"
                                            value={formData.location}
                                            onChange={handleChange}
                                            placeholder="e.g., Main Entrance, Office Reception"
                                        />
                                        {validationErrors.location && (
                                            <div className="invalid-feedback">{validationErrors.location}</div>
                                        )}
                                        <div className="form-text">Physical location of the device</div>
                                    </div>

                                    <div className="mb-3">
                                        <div className="form-check form-switch">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                role="switch"
                                                id="is_active"
                                                name="is_active"
                                                checked={formData.is_active === 1}
                                                onChange={handleChange}
                                            />
                                            <label className="form-check-label fw-medium" htmlFor="is_active">
                                                Active Device
                                            </label>
                                        </div>
                                        <div className="form-text">Only active devices will be monitored</div>
                                    </div>
                                </div>

                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-light border-secondary-subtle"
                                        onClick={() => setShowAddModal(false)}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-primary text-white"
                                    >
                                        <i className="bi bi-plus-circle me-2"></i>
                                        Add Device
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Device Modal */}
            {showEditModal && selectedDevice && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header" style={{
                                background: 'linear-gradient(135deg, #0d6efd 0%, #0b5ed7 100%)',
                                borderBottom: 'none'
                            }}>
                                <h5 className="modal-title text-white">
                                    <i className="bi bi-pencil-square me-2"></i>
                                    Edit Device: {selectedDevice.device_name}
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    onClick={() => setShowEditModal(false)}
                                ></button>
                            </div>

                            <form onSubmit={handleEditSubmit}>
                                <div className="modal-body">
                                    <div className="mb-3">
                                        <label htmlFor="device_name" className="form-label fw-medium">
                                            Device Name <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-control ${validationErrors.device_name ? 'is-invalid' : ''}`}
                                            id="device_name"
                                            name="device_name"
                                            value={formData.device_name}
                                            onChange={handleChange}
                                            required
                                        />
                                        {validationErrors.device_name && (
                                            <div className="invalid-feedback">{validationErrors.device_name}</div>
                                        )}
                                    </div>

                                    <div className="row">
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <label htmlFor="ip_address" className="form-label fw-medium">
                                                    IP Address <span className="text-danger">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    className={`form-control ${validationErrors.ip_address ? 'is-invalid' : ''}`}
                                                    id="ip_address"
                                                    name="ip_address"
                                                    value={formData.ip_address}
                                                    onChange={handleChange}
                                                    required
                                                />
                                                {validationErrors.ip_address && (
                                                    <div className="invalid-feedback">{validationErrors.ip_address}</div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <label htmlFor="port" className="form-label fw-medium">
                                                    Port <span className="text-danger">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    className={`form-control ${validationErrors.port ? 'is-invalid' : ''}`}
                                                    id="port"
                                                    name="port"
                                                    value={formData.port}
                                                    onChange={handleChange}
                                                    required
                                                />
                                                {validationErrors.port && (
                                                    <div className="invalid-feedback">{validationErrors.port}</div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mb-3">
                                        <label htmlFor="location" className="form-label fw-medium">
                                            Location
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-control ${validationErrors.location ? 'is-invalid' : ''}`}
                                            id="location"
                                            name="location"
                                            value={formData.location}
                                            onChange={handleChange}
                                        />
                                        {validationErrors.location && (
                                            <div className="invalid-feedback">{validationErrors.location}</div>
                                        )}
                                    </div>

                                    <div className="mb-3">
                                        <div className="form-check form-switch">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                role="switch"
                                                id="is_active"
                                                name="is_active"
                                                checked={formData.is_active === 1}
                                                onChange={handleChange}
                                            />
                                            <label className="form-check-label fw-medium" htmlFor="is_active">
                                                Active Device
                                            </label>
                                        </div>
                                    </div>
                                </div>

                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-light border-secondary-subtle"
                                        onClick={() => setShowEditModal(false)}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-primary text-white"
                                    >
                                        <i className="bi bi-check-circle me-2"></i>
                                        Update Device
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* View Device Modal */}
            {showViewModal && selectedDevice && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header bg-info text-white">
                                <h5 className="modal-title">
                                    <i className="bi bi-eye me-2"></i>
                                    Device Details: {selectedDevice.device_name}
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    onClick={() => setShowViewModal(false)}
                                ></button>
                            </div>

                            <div className="modal-body">
                                <div className="row">
                                    <div className="col-md-6">
                                        <table className="table table-sm">
                                            <tbody>
                                                <tr>
                                                    <th className="text-muted" width="40%">Device Name:</th>
                                                    <td>{selectedDevice.device_name}</td>
                                                </tr>
                                                <tr>
                                                    <th className="text-muted">IP Address:</th>
                                                    <td>
                                                        <span className="badge bg-primary">{selectedDevice.ip_address}</span>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <th className="text-muted">Port:</th>
                                                    <td>
                                                        <span className="badge bg-secondary">{selectedDevice.port}</span>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <th className="text-muted">Status:</th>
                                                    <td>{getStatusBadge(selectedDevice.is_active)}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="col-md-6">
                                        <table className="table table-sm">
                                            <tbody>
                                                <tr>
                                                    <th className="text-muted" width="40%">Location:</th>
                                                    <td>{selectedDevice.location || 'Not specified'}</td>
                                                </tr>
                                                <tr>
                                                    <th className="text-muted">Device ID:</th>
                                                    <td>{selectedDevice.device_id}</td>
                                                </tr>
                                                <tr>
                                                    <th className="text-muted">Company ID:</th>
                                                    <td>{selectedDevice.company_id}</td>
                                                </tr>
                                                <tr>
                                                    <th className="text-muted">Connection:</th>
                                                    <td>
                                                        {getConnectionBadge(selectedDevice)}
                                                        <br />
                                                        <small className="text-muted">
                                                            Last checked: {getLastCheckedTime(selectedDevice)}
                                                        </small>
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                <div className="mt-3">
                                    <div className={`alert ${connectionStatus[selectedDevice.device_id]?.status === 'online' ? 'alert-success' : 'alert-warning'}`} role="alert">
                                        <i className="bi bi-info-circle me-2"></i>
                                        <strong>Connection Details:</strong> {selectedDevice.ip_address}:{selectedDevice.port}
                                        {connectionStatus[selectedDevice.device_id]?.message && (
                                            <div className="mt-1">
                                                <small>{connectionStatus[selectedDevice.device_id].message}</small>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setShowViewModal(false)}
                                >
                                    Close
                                </button>
                                {canEditDevice() && (
                                    <button
                                        type="button"
                                        className="btn btn-warning"
                                        onClick={() => handleTestConnection(selectedDevice)}
                                        disabled={testingConnections[selectedDevice.device_id]}
                                    >
                                        <i className={`bi ${testingConnections[selectedDevice.device_id] ? 'bi-arrow-clockwise' : 'bi-wifi'} me-2`}></i>
                                        {testingConnections[selectedDevice.device_id] ? 'Testing...' : 'Test Connection'}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && selectedDevice && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header bg-danger text-white">
                                <h5 className="modal-title">
                                    <i className="bi bi-exclamation-triangle me-2"></i>
                                    Confirm Delete
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    onClick={() => setShowDeleteModal(false)}
                                ></button>
                            </div>

                            <div className="modal-body">
                                <div className="alert alert-warning" role="alert">
                                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                                    <strong>Warning:</strong> This action cannot be undone.
                                </div>

                                <p>Are you sure you want to delete the following device?</p>

                                <div className="card border-0 bg-light">
                                    <div className="card-body">
                                        <h6 className="fw-bold">
                                            <i className={`bi ${getDeviceIcon(selectedDevice.device_name)} me-2`}></i>
                                            {selectedDevice.device_name}
                                        </h6>
                                        <div className="row">
                                            <div className="col-md-6">
                                                <small className="text-muted d-block">IP Address</small>
                                                <strong className="text-primary">{selectedDevice.ip_address}</strong>
                                            </div>
                                            <div className="col-md-6">
                                                <small className="text-muted d-block">Port</small>
                                                <strong>{selectedDevice.port}</strong>
                                            </div>
                                        </div>
                                        <div className="row mt-2">
                                            <div className="col-md-6">
                                                <small className="text-muted d-block">Location</small>
                                                <strong>{selectedDevice.location || 'N/A'}</strong>
                                            </div>
                                            <div className="col-md-6">
                                                <small className="text-muted d-block">Status</small>
                                                <strong>{selectedDevice.is_active ? 'Active' : 'Inactive'}</strong>
                                            </div>
                                        </div>
                                        <div className="row mt-2">
                                            <div className="col-12">
                                                <small className="text-muted d-block">Connection</small>
                                                {getConnectionBadge(selectedDevice)}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="alert alert-danger mt-3" role="alert">
                                    <i className="bi bi-info-circle me-2"></i>
                                    <strong>Note:</strong> Deleting this device will remove it from the system.
                                </div>
                            </div>

                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-light border-secondary-subtle"
                                    onClick={() => setShowDeleteModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-danger"
                                    onClick={handleDeleteConfirm}
                                >
                                    <i className="bi bi-trash me-2"></i>
                                    Delete Device
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Device;